<template>
  <div
    class="patient-management"
    :class="loadingContent ? 'loading large' : ''"
  >
    <lila-message :alert-data="alert" />

    <div
      v-show="!loadingContent"
      class="pm-fields"
    >
      <h2 class="pm-title">
        Gestão do Paciente
      </h2>

      <div class="pm-row">
        <label class="pm-label">Equipe</label>
        <div class="pm-team-picker-wrap">
          <button
            type="button"
            class="pm-team-select"
            @click.stop="teamPickerOpen = !teamPickerOpen"
          >
            <span :class="{ 'is-placeholder': !selectedTeamName }">
              {{ selectedTeamName || "Selecione" }}
            </span>
            <span class="pm-team-caret" />
          </button>
          <div
            v-if="teamPickerOpen"
            class="pm-team-picker"
          >
            <team-tree-picker
              :tree="teamTree"
              :immediate="true"
              :units-selectable="false"
              :current-id="form.teamId"
              title="Trocar equipe"
              @select="pickTeam"
              @cancel="teamPickerOpen = false"
            />
          </div>
        </div>
      </div>

      <div class="pm-row pm-row--inline">
        <label class="pm-label">Licenciado</label>
        <label class="pm-switch">
          <input
            type="checkbox"
            :checked="form.isLicensed"
            :disabled="saving"
            @change="form.isLicensed = $event.target.checked"
          >
          <span class="pm-slider" />
        </label>
      </div>

      <div class="pm-actions">
        <lila-button
          type="button"
          title="Salvar"
          :loading="saving"
          @custom-click-event="save"
        />
      </div>

      <!-- POC Cuidados Paliativos: enviar para casa (convite do app) / readmitir -->
      <lila-palliative-home-transfer
        v-if="$Utilities.hasPalliativeCareAccess(this)"
        :patient-id="$route.params.id"
        :patient-name="patient.name"
      />
    </div>
  </div>
</template>

<script>
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";
import PalliativeHomeTransfer from "@components/patients/PalliativeHomeTransfer.vue";

export default {
  components: {
    "lila-button": Button,
    "lila-message": Message,
    "team-tree-picker": TeamTreePicker,
    "lila-palliative-home-transfer": PalliativeHomeTransfer
  },
  emits: ["update-patient-team"],
  data() {
    return {
      loadingContent: false,
      saving: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      teams: [],
      // Equipes de destino válidas (api/team/transfer-targets, escopadas),
      // usadas para montar a árvore agrupada do seletor.
      transferTargets: [],
      teamPickerOpen: false,
      patient: {
        id: null,
        name: "",
        teamId: null,
        isLicensed: false
      },
      // Estado editável do formulário.
      form: {
        teamId: null,
        isLicensed: false
      }
    };
  },
  computed: {
    // Nome da equipe selecionada (mostrado no botão-gatilho).
    selectedTeamName() {
      if (this.form.teamId == null) return "";
      const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, this.form.teamId);
      return t ? t.name : "";
    },
    // Árvore Unidades › Equipes a partir dos transfer-targets (com Path).
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    },
    patientId() {
      return this.$route.params.id;
    }
  },
  watch: {
    // Recarrega ao trocar de paciente sem sair da aba.
    patientId() {
      this.loadPatient();
    }
  },
  created() {
    this.getTeams();
    this.getTransferTargets();
    this.loadPatient();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },

    async getTeams() {
      try {
        const res = await this.$axios.get("api/team", {
          headers: this.authHeader()
        });
        this.teams = (res.data || []).map((item) => ({
          id: item.Id,
          name: item.Name
        }));
      } catch (error) {
        // Filtro opcional; não bloqueia a tela.
        console.error("Erro ao carregar equipes:", error);
      }
    },

    // Equipes de destino (api/team/transfer-targets), escopadas pela árvore
    // do profissional e com o Path das unidades ancestrais para agrupar a árvore.
    async getTransferTargets() {
      try {
        const res = await this.$axios.get("api/team/transfer-targets", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.transferTargets = list.filter(Boolean).map((item) => ({
          id: this.$Utilities.getValueIgnoreCase(item, "Id"),
          name: this.$Utilities.getValueIgnoreCase(item, "Name"),
          path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map(
            (unit) => ({
              id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
              name: this.$Utilities.getValueIgnoreCase(unit, "Name")
            })
          )
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes de destino:", error);
      }
    },

    // Seleção no picker (immediate): atualiza o form e fecha. O Salvar persiste.
    pickTeam(id) {
      this.form.teamId = id;
      this.teamPickerOpen = false;
    },

    handleClickOutside(event) {
      if (!this.teamPickerOpen) return;
      const picker = document.querySelector(".pm-team-picker");
      if (picker && !picker.contains(event.target)) {
        this.teamPickerOpen = false;
      }
    },

    // Resolve o Id da equipe do paciente tolerando os formatos que o backend
    // pode retornar. No Lila.API a equipe vem dentro de "Doctor" (Doctor com
    // IsTeam=true); também aceita "Team" {Id/id} ou o escalar TeamId.
    resolveTeamId(item) {
      const doctor = this.$Utilities.getValueIgnoreCase(item, "Doctor");
      if (doctor && typeof doctor === "object") {
        const id = this.$Utilities.getValueIgnoreCase(doctor, "Id");
        if (id != null) return id;
      }
      const team = this.$Utilities.getValueIgnoreCase(item, "Team");
      if (team && typeof team === "object") {
        const id = this.$Utilities.getValueIgnoreCase(team, "Id");
        if (id != null) return id;
      }
      const teamId = this.$Utilities.getValueIgnoreCase(item, "TeamId");
      return teamId != null ? teamId : null;
    },

    async loadPatient() {
      if (!this.patientId) return;
      this.loadingContent = true;
      try {
        const res = await this.$axios.get(`/api/patient/${this.patientId}`, {
          headers: this.authHeader()
        });
        const item = res.data || {};

        this.patient = {
          id: item.Id,
          name: item.Name || "",
          teamId: this.resolveTeamId(item),
          isLicensed: Boolean(
            this.$Utilities.getValueIgnoreCase(item, "IsLicensed") ??
            this.$Utilities.getValueIgnoreCase(item, "Licensed")
          )
        };

        this.form.teamId = this.patient.teamId;
        this.form.isLicensed = this.patient.isLicensed;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingContent = false;
      }
    },

    async save() {
      if (!this.form.teamId) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "warning",
          "Selecione uma equipe."
        );
        return;
      }

      this.saving = true;
      try {
        await Promise.all([
          // Troca de equipe — PUT api/Patient/{patientId}/team.
          this.$axios.put(
            `api/Patient/${this.patient.id}/team`,
            {
              TeamId: this.form.teamId
            },
            { headers: this.authHeader() }
          ),
          // Altera a licença (liga/desliga) — PUT api/Patient/{patientId}/license.
          this.$axios.put(
            `api/Patient/${this.patient.id}/license`,
            {
              IsLicensed: this.form.isLicensed
            },
            { headers: this.authHeader() }
          )
        ]);

        this.patient.teamId = this.form.teamId;
        this.patient.isLicensed = this.form.isLicensed;

        // Avisa o pai (lista + card do paciente) pra refletir a nova equipe
        // sem precisar recarregar a página.
        this.$emit("update-patient-team", {
          teamId: this.form.teamId,
          teamName: this.selectedTeamName
        });

        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          "Dados do paciente atualizados com sucesso."
        );
      } catch (error) {
        this.handleError(
          error,
          "Não foi possível salvar as alterações do paciente."
        );
      } finally {
        this.saving = false;
      }
    },

    handleError(
      error,
      message = "Ocorreu um erro ao carregar as informações do paciente."
    ) {
      const data = error && error.response ? error.response.data : null;
      if (error && error.response) {
        this.$Utilities.verifyToken(error.response.status);
      }
      this.$Utilities.controlAlert(data, this.alert, "danger", message);
    }
  }
};
</script>

<style lang="scss" scoped>
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

.patient-management {
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
}

.pm-fields {
  max-width: 480px;
}

.pm-title {
  color: $lila-primary;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 20px;
}

.pm-row {
  margin-bottom: 18px;
}

.pm-row--inline {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pm-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

.pm-row--inline .pm-label {
  margin-bottom: 0;
}

/* Seletor de equipe: botão-gatilho + popover do picker (padrão do dropdown) */
.pm-team-picker-wrap {
  position: relative;
}

.pm-team-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  padding: 11px 20px;
  font-size: 14px;
  color: #3a3a3a;
  cursor: pointer;
  font-family: inherit;
}

.pm-team-select .is-placeholder {
  color: #9a95a8;
}

.pm-team-caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid $lila-accent;
  flex: none;
}

.pm-team-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

/* Toggle "Licenciado" */
.pm-switch {
  display: inline-block;
  height: 22px;
  width: 45px;
  position: relative;
}

.pm-switch input {
  display: none;
}

.pm-slider {
  background-color: #e0e0e0;
  border-radius: 34px;
  position: absolute;
  inset: 0;
  cursor: pointer;
  transition: 0.4s;
}

.pm-slider:before {
  content: "";
  background-color: #ffffff;
  border-radius: 50%;
  height: 16px;
  width: 16px;
  bottom: 3px;
  left: 4px;
  position: absolute;
  transition: 0.4s;
}

.pm-switch input:checked + .pm-slider {
  background-color: $lila-primary;
}

.pm-switch input:checked + .pm-slider:before {
  transform: translateX(21px);
}

.pm-switch input:disabled + .pm-slider {
  opacity: 0.6;
  cursor: default;
}

.pm-actions {
  margin-top: 24px;
}
</style>
